import { ScrollableRegion } from '@syncanix/ui';

import { GradientText } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { isolatePaths } from '@/lib/bidi/isolate-paths';

import type { Comparison } from '@/features/comparisons';
import type { ReactElement } from 'react';

/**
 * The at-a-glance table — the page's centre of gravity.
 *
 * ## The Syncanix column is a FEATURED column, not a tinted one
 *
 * It previously carried `--bd-tint` (#eef3ff, four points off the page) and a
 * 3px rule on the header cell. At a normal reading distance that is not a
 * marked column; it is a column somebody nearly marked. The reader gets no help
 * scanning one side at a time, which is the entire job of the third column on a
 * comparison table.
 *
 * It now reads the way a pricing table's recommended plan does — the shape
 * every visitor already knows:
 *
 * - **A continuous side rule down both edges** of the column, drawn on the
 *   cells rather than the table, so it survives the horizontal scroll.
 * - **The brand rule across the top**, above the sticky header's own bottom
 *   border rather than fighting it.
 * - **The wordmark in the brand's text gradient** (`--bd-gradient-text`, the
 *   darkened stops that are contrast-tested for a light ground — never the fill
 *   gradient, which is a background and fails as ink).
 * - **Ink one step darker than the rival column**, which is the only
 *   typographic difference between them.
 *
 * ## What is deliberately NOT done
 *
 * No tick/cross iconography, no "winner" marks, no colour-coding of cells by
 * who does better. Both columns take the same type at the same size, and the
 * rival's sentences are the catalog's own — several of which concede the row
 * outright. Marking WHICH column is ours is orientation on a page the reader
 * chose by name; marking which column is BETTER, row by row, would be the
 * scoreboard this page was rewritten to stop being.
 *
 * ## Row labels recede
 *
 * The feature label is the question, not the answer. It moves to the same mono
 * caption treatment as the column header so the two data columns carry the
 * weight — before, all three columns competed at the same density and the eye
 * had nowhere to land.
 */
export function GlanceTable({ data }: { readonly data: Comparison }): ReactElement {
  const { t } = useT();

  // The featured column's GROUND + side rules, without padding — the header
  // cell and the body cells pad differently, and a shared string that has to be
  // string-edited at one call site is a class list waiting to be broken by a
  // rename. The two pseudo-elements draw the column's edges on every cell, so
  // the rule is continuous through a scroll rather than clipped to the table.
  const ourColumn =
    'relative bg-[color:var(--bd-tint)] ' +
    'before:pointer-events-none before:absolute before:inset-y-0 before:start-0 before:w-px ' +
    'before:bg-[color:var(--bd-line-3)] after:pointer-events-none after:absolute ' +
    'after:inset-y-0 after:end-0 after:w-px after:bg-[color:var(--bd-line-3)]';
  const ourCell = `${ourColumn} px-5 py-4 text-[color:var(--bd-ink)]`;
  const theirCell = 'px-5 py-4 text-[color:var(--bd-body)]';
  const stickyHead =
    'sticky top-0 z-20 border-b-2 border-[color:var(--bd-line-3)] pb-3 pt-4 text-start align-bottom';

  return (
    <ScrollableRegion
      aria-label={t('vs.detail.atAGlance')}
      className="mt-7 overflow-auto rounded-[var(--bd-radius)] border border-[color:var(--bd-line-2)] [contain:paint]"
      style={{ maxHeight: '40rem' }}
    >
      {/* named by the section h2 so the table rotor announces it (I153) */}
      <table aria-labelledby="at-a-glance" className="w-full border-collapse text-start text-sm">
        <thead>
          <tr>
            <th
              scope="col"
              className={`${stickyHead} min-w-[11rem] bg-[color:var(--bd-page)] px-5 font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.13em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal`}
            >
              {t('vs.detail.columnFeature')}
            </th>
            <th
              scope="col"
              className={`${stickyHead} min-w-[13rem] bg-[color:var(--bd-page)] px-5 text-[1.05rem] font-semibold tracking-[-0.022em] text-[color:var(--bd-muted)]`}
            >
              {data.rivalName}
            </th>
            <th
              scope="col"
              className={`${stickyHead} ${ourColumn} min-w-[13rem] px-5 text-[1.05rem] font-semibold tracking-[-0.022em]`}
            >
              {/* The brand rule marks our column. `inset-x-0` inside the cell
                  rather than a border, so it sits above the sticky header's own
                  bottom rule instead of fighting it. */}
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-[3px]"
                style={{ background: 'var(--bd-gradient)' }}
              />
              <GradientText>Syncanix</GradientText>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr
              key={row.featureKey}
              className="group border-b border-[color:var(--bd-line)] align-top last:border-b-0"
            >
              <th
                scope="row"
                className="px-5 py-4 text-start align-top font-mono rtl:font-sans text-[10.5px] uppercase leading-[1.7] tracking-[0.11em] text-[color:var(--bd-faint)] transition-colors group-hover:bg-[color:var(--bd-sunk)] rtl:normal-case rtl:tracking-normal"
              >
                {t(row.featureKey)}
              </th>
              <td
                className={`${theirCell} leading-[1.6] transition-colors group-hover:bg-[color:var(--bd-sunk)]`}
              >
                {isolatePaths(t(row.rivalKey))}
              </td>
              <td className={`${ourCell} font-medium leading-[1.6]`}>
                {isolatePaths(t(row.syncanixKey))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollableRegion>
  );
}
